/**
 * VenturaZ — Brand shape library.
 *
 * Six brand-guideline shapes (Comprehensive, Idea, Growth, Clarity, Knowledge,
 * Network) rendered as inline SVGs. Each accepts { size, color, opacity,
 * strokeWidth, className, style } props so they can be reused as marketing
 * backgrounds, section motifs, or thumbnail icons.
 *
 * All shapes are outline-only (no fill) so they look elegant on any tint.
 */
import React from "react";

const defaults = {
  size: 200,
  color: "currentColor",
  opacity: 1,
  strokeWidth: 1.2,
  style: undefined,
  className: "",
};

/** Concentric circles — knowledge feeding outward. */
export function Comprehensive(props) {
  const { size, color, opacity, strokeWidth, style, className } = { ...defaults, ...props };
  const rings = [90, 74, 58, 42, 26, 10];
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} className={className} style={{ opacity, ...style }}>
      {rings.map((r) => (
        <circle key={r} cx="100" cy="100" r={r} fill="none" stroke={color} strokeWidth={strokeWidth} />
      ))}
    </svg>
  );
}

/** Stacked arcs — the "bulb" of an emerging idea. */
export function Idea(props) {
  const { size, color, opacity, strokeWidth, style, className } = { ...defaults, ...props };
  const arcs = [
    "M20,120 A80,120 0 0 1 180,120",
    "M32,120 A68,102 0 0 1 168,120",
    "M46,120 A54,84 0 0 1 154,120",
    "M60,120 A40,66 0 0 1 140,120",
    "M74,120 A26,48 0 0 1 126,120",
    "M88,120 A12,30 0 0 1 112,120",
  ];
  return (
    <svg viewBox="0 0 200 160" width={size} height={size * 0.8} className={className} style={{ opacity, ...style }}>
      {arcs.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={color} strokeWidth={strokeWidth} />
      ))}
      <circle cx="100" cy="24" r="4" fill={color} />
    </svg>
  );
}

/** Three chevrons forward — accelerating growth. */
export function Growth(props) {
  const { size, color, opacity, strokeWidth, style, className } = { ...defaults, ...props };
  const chevrons = [10, 60, 110];
  return (
    <svg viewBox="0 0 200 160" width={size} height={size * 0.8} className={className} style={{ opacity, ...style }}>
      {chevrons.map((x) => (
        <g key={x} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round">
          <polyline points={`${x},20 ${x + 44},80 ${x},140`} />
          <polyline points={`${x},34 ${x + 32},80 ${x},126`} />
        </g>
      ))}
    </svg>
  );
}

/** Concentric diamonds — precision and clarity. */
export function Clarity(props) {
  const { size, color, opacity, strokeWidth, style, className } = { ...defaults, ...props };
  const sizes = [90, 74, 58, 42, 26, 10];
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} className={className} style={{ opacity, ...style }}>
      {sizes.map((s) => (
        <polygon
          key={s}
          points={`100,${100 - s} ${100 + s},100 100,${100 + s} ${100 - s},100`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
        />
      ))}
    </svg>
  );
}

/** Concentric hexagons — layered knowledge. */
export function Knowledge(props) {
  const { size, color, opacity, strokeWidth, style, className } = { ...defaults, ...props };
  const radii = [90, 74, 58, 42, 26, 10];
  const hex = (cx, cy, r) => {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      points.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
    }
    return points.join(" ");
  };
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} className={className} style={{ opacity, ...style }}>
      {radii.map((r) => (
        <polygon key={r} points={hex(100, 100, r)} fill="none" stroke={color} strokeWidth={strokeWidth} />
      ))}
    </svg>
  );
}

/** Dense diamond mesh — a network of nodes. */
export function Network(props) {
  const { size, color, opacity, strokeWidth, style, className } = { ...defaults, ...props };
  const lines = [];
  const step = 8;
  const clip = 90;
  for (let i = -clip; i <= clip; i += step) {
    lines.push(`M${100 + i},${10} L${100 + i},${190}`);
    lines.push(`M${10},${100 + i} L${190},${100 + i}`);
  }
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} className={className} style={{ opacity, ...style }}>
      <defs>
        <clipPath id="diamond-clip">
          <polygon points="100,10 190,100 100,190 10,100" />
        </clipPath>
      </defs>
      <g clipPath="url(#diamond-clip)" transform="rotate(45 100 100)">
        {lines.map((d, i) => (
          <path key={i} d={d} stroke={color} strokeWidth={strokeWidth * 0.6} fill="none" />
        ))}
      </g>
      <polygon points="100,10 190,100 100,190 10,100" fill="none" stroke={color} strokeWidth={strokeWidth} />
    </svg>
  );
}

/**
 * Repeating chevron pattern for large hero backgrounds (mimics the "How we look"
 * layout in the brand guide). Rendered as an SVG that tiles horizontally.
 */
export function ChevronField({ color = "#FFFFFF", opacity = 0.18, className = "", style = undefined }) {
  return (
    <svg
      viewBox="0 0 800 400"
      preserveAspectRatio="xMidYMid slice"
      className={className}
      style={{ opacity, ...style }}
      aria-hidden="true"
    >
      {[0, 140, 280, 420, 560, 700].map((x, i) => (
        <g key={x} fill="none" stroke={color} strokeWidth="6" strokeLinejoin="round" opacity={0.35 + i * 0.12}>
          <polyline points={`${x},40 ${x + 90},200 ${x},360`} />
          <polyline points={`${x + 40},40 ${x + 130},200 ${x + 40},360`} strokeWidth="4" />
        </g>
      ))}
    </svg>
  );
}

/**
 * Repeating brand-shape pattern used on VenturaZ stationery — a subtle mesh of
 * mixed motifs. Best used at 15–25% opacity behind hero cards.
 */
export function BrandMesh({ color = "#FFFFFF", opacity = 0.14, className = "", style = undefined }) {
  return (
    <svg
      viewBox="0 0 400 400"
      preserveAspectRatio="xMidYMid slice"
      className={className}
      style={{ opacity, ...style }}
      aria-hidden="true"
    >
      <defs>
        <pattern id="venturaz-mesh" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
          <circle cx="40" cy="40" r="14" fill="none" stroke={color} strokeWidth="1.1" />
          <polygon points="40,10 70,40 40,70 10,40" fill="none" stroke={color} strokeWidth="1.1" />
          <rect x="30" y="30" width="20" height="20" fill="none" stroke={color} strokeWidth="1.1" />
          <path d="M0,40 L80,40 M40,0 L40,80" stroke={color} strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="400" height="400" fill="url(#venturaz-mesh)" />
    </svg>
  );
}

const shapes = { Comprehensive, Idea, Growth, Clarity, Knowledge, Network, ChevronField, BrandMesh };
export default shapes;
